import { useCallback, useEffect, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { api, quarterLabel, type EditOutcome, type Site, type SiteQuarter } from '../lib/api';
import { quarterDates, shiftQuarter } from '../lib/dates';
import { navigate } from '../lib/router';
import { ScheduleGrid, printPages } from './ScheduleGrid';

/**
 * The admin home page (D65): every hospital's month, one grid each, days across and positions
 * down — the shape John reads a rota in, and the same grid the Hospitals tab shows for one
 * hospital (D64), so the two cannot drift apart.
 *
 * The dashboard that used to live here is still there, under its own tab: this page answers
 * "what does the month look like everywhere", and that one answers "what needs me today".
 */
export function AllHospitals({ quarter, sites, editedBy }: { quarter: string; sites: Site[]; editedBy: string }) {
  const [data, setData] = useState<{ quarter: string; sites: SiteQuarter[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback((blank: boolean) => {
    if (blank) { setData(null); setError(null); }
    api.allSitesQuarter(quarter).then(setData).catch((e: Error) => setError(e.message));
  }, [quarter]);
  useEffect(() => load(true), [load]);

  // A shift can be changed straight from this page (D71) — the same dialogs the week page
  // opens. The whole quarter is refetched afterwards because one edit can move a second shift
  // and can change what is flagged at another hospital's week entirely.
  // Reloading is not done here: every shift edit announces itself and `useReloadOnChange` below
  // refetches, so a change made on ANY screen lands on this one too — not only the ones made
  // here (D93). Doing both would fetch the same quarter twice for one edit.
  const changed = (o: EditOutcome, what: string) => {
    setToast(`${what}. ${o.introduced.length ? `${o.introduced.length} error(s) overridden.` : 'No new errors.'}`);
  };
  useReloadOnChange(() => load(false));
  const editingFor = (siteId: string) => {
    const site = sites.find((x) => x.id === siteId);
    return site && editedBy ? { site, editedBy, onChanged: changed } : undefined;
  };

  const go = (q: string) => navigate(`/admin?quarter=${q}`);

  // Every hospital that has a shape gets a grid, whether or not anything is scheduled in it
  // (D105): its positions come from what it is expected to staff, not only from the shifts
  // that exist, so an empty quarter draws as a grid of open cells. Only a hospital with no
  // coverage expectation at all — a sign-up marketplace — has no positions, and those are
  // named in the line below instead.
  //
  // This comment used to say a quiet hospital "still gets a heading". It did not: it got its
  // name in a sentence at the foot of the page, forty lines from here. James read the claim
  // in a tip sheet written off this comment and asked when it had been built.
  const withWork = data?.sites.filter((s) => s.slots.length > 0) ?? [];
  const quiet = data?.sites.filter((s) => s.slots.length === 0) ?? [];

  return (
    <section className="allhospitals">
      <style>{'@media print { @page { size: landscape; } }'}</style>
      <div className="gridtools noprint">
        <div className="quarterpicker">
          <button type="button" onClick={() => go(shiftQuarter(quarter, -1))} aria-label="Previous quarter">‹</button>
          <h1>{quarterLabel(quarter)} <span className="quarterspan">{quarterDates(quarter)}</span></h1>
          <button type="button" onClick={() => go(shiftQuarter(quarter, 1))} aria-label="Next quarter">›</button>
        </div>
        <button className="linklike" type="button" onClick={() => window.print()}>print</button>
      </div>
      {toast && <p className="toast noprint" onClick={() => setToast(null)}>{toast}</p>}
      {!editedBy && <p className="muted small noprint">Enter your name at the top of the page to change a shift from here — every edit is recorded with who made it.</p>}
      {error && <p className="error">Couldn't load this month ({error}).</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <>
          <h1 className="printonly">Every hospital — {quarterLabel(quarter)}</h1>
          {withWork.length === 0 && <p className="muted">Nothing is scheduled at any hospital in {quarterLabel(quarter)}.</p>}
          {withWork.map((s) => (
            <div key={s.site.id} className="hospitalblock">
              <h2>
                <button type="button" className="linklike" onClick={() => navigate(`/admin/hospitals/${s.site.id}?quarter=${quarter}`)}>{s.site.name}</button>
                <span className="muted small"> · {s.slots.length} {s.slots.length === 1 ? 'position' : 'positions'}</span>
              </h2>
              <div className={`gridscroll sitetint site-${s.site.id} noprint`}>
                <ScheduleGrid siteId={s.site.id} slots={s.slots} days={s.days} editing={editingFor(s.site.id)} />
              </div>
              {/* On paper the month is split, because a table cannot break across its columns (D64). */}
              <div className={`printonly sitetint site-${s.site.id}`}>
                {printPages(s.days).map((part: SiteQuarter['days'], i: number) => (
                  <div key={i} className="printhalf">
                    <ScheduleGrid siteId={s.site.id} slots={s.slots} days={part} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {quiet.length > 0 && (
            <p className="muted small">
              No positions are configured at {quiet.map((s) => s.site.name).join(', ')}, so there is nothing to lay out.
            </p>
          )}
        </>
      )}
    </section>
  );
}
